import { useState } from "react";
import { createBoqItemsApi } from "../../services/api/boq/boqService";
import { toast } from "react-hot-toast";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";

export default function BoqItemsPage({ onSaved }) {
  const { id } = useParams();
  const location = useLocation();
  const passedBoqName = location.state?.boqName || "";
  const navigate = useNavigate();
  const [items, setItems] = useState([
    {
      sn: "",
      item_name: "",
      description: "",
      unit: "",
      quantity: "",
      rate: "",
      total_amount: "",
      //scope: "",
      scope: passedBoqName,
      approved_make: "",
      offered_make: "",
    },
  ]);
  const [errors, setErrors] = useState({});

  const handleChange = (index, field, value) => {
    const updatedItems = [...items];
    updatedItems[index][field] = value;
    if (field === "quantity" || field === "rate") {
      const qty = parseFloat(updatedItems[index].quantity);
      const rate = parseFloat(updatedItems[index].rate);

      if (!isNaN(qty) && !isNaN(rate)) {
        updatedItems[index].total_amount = (qty * rate).toFixed(2);
      } else {
        updatedItems[index].total_amount = "";
      }
    }
    setItems(updatedItems);
  };

  const addItem = () => {
    setItems([
      ...items,
      {
        sn: "",
        item_name: "",
        description: "",
        unit: "",
        quantity: "",
        rate: "",
        total_amount: "",
        // scope: "",
        scope: passedBoqName,
        approved_make: "",
        offered_make: "",
      },
    ]);
  };

  const removeItem = (index) => {
    setItems(items.filter((_, i) => i !== index));
  };

  const validate = () => {
    const newErrors = {};

    items.forEach((item, idx) => {
      const itemErrors = {};

      if (!item.sn) itemErrors.sn = "SN is required";
      if (!item.item_name) itemErrors.item_name = "Item name is required";
      if (!item.description) itemErrors.description = "Description is required";
      if (!item.unit) itemErrors.unit = "Unit is required";

      // Qty optional but must not be negative
      if (item.quantity && item.quantity < 0) {
        itemErrors.quantity = "Quantity cannot be negative";
      }

      // Rate optional but must not be negative
      if (item.rate && item.rate < 0) {
        itemErrors.rate = "Rate cannot be negative";
      }

      if (Object.keys(itemErrors).length > 0) {
        newErrors[idx] = itemErrors;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const saveItems = async () => {
    if (!validate()) return;
    try {
      const payload = items.map((item) => ({
        boq_id: id,
        sn: item.sn || "",
        item_name: item.item_name || "",
        description: item.description || "",
        unit: item.unit || "",
        quantity: parseFloat(item.quantity) || 0,
        rate: parseFloat(item.rate) || 0,
        total_amount: parseFloat(item.total_amount) || 0,
        scope: item.scope || "",
        approved_make: item.approved_make || "",
        offered_make: item.offered_make || "",
      }));

      const data = await createBoqItemsApi(id, payload);

      if (data.status) {
        toast.success(data.message || "BOQ Items saved successfully!");
        onSaved?.();
        navigate(`/boqs/${id}`);
        setItems([
          {
            sn: "",
            item_name: "",
            description: "",
            unit: "",
            quantity: "",
            rate: "",
            total_amount: "",
            scope: "",
            approved_make: "",
            offered_make: "",
          },
        ]);
      }
    } catch (err) {
      console.error(err);
      toast.error("Failed to save BOQ Items. Please try again.");
    }
  };

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const data = XLSX.utils.sheet_to_json(ws, { defval: "" });

      const importedItems = data.map((row) => {
        const qty = parseFloat(row.Quantity) || 0;
        const rate = parseFloat(row.Rate) || 0;
        return {
          boq_id: id,
          sn: row.SN || "",
          item_name: row["Item Name"] || "",
          description: row.Description || "",
          unit: row.Unit || "",
          quantity: row.Quantity || "",
          rate: row.Rate || "",
          total_amount: qty && rate ? (qty * rate).toFixed(2) : "",
          scope: row.Scope || "",
          approved_make: row["Approved Make"] || "",
          offered_make: row["Offered Make"] || "",
        };
      });
      setItems(importedItems);
      toast.success(`${importedItems.length} items imported successfully!`);
    };
    reader.readAsBinaryString(file);
  };

  const downloadTemplate = () => {
    const templateData = [
      {
        SN: "1",
        "Item Name": "Sample Item",
        Description: "Description of item",
        Unit: "Nos",
        Quantity: 10,
        Rate: 100,
        Scope: "Scope of work",
        "Approved Make": "Brand A",
        "Offered Make": "Brand B",
      },
    ];
    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "BOQ_Template");
    XLSX.writeFile(wb, "BOQ_Items_Template.xlsx");
  };

  return (
    <div className="max-w-7xl mx-auto p-4 space-y-6">
      {/* Excel Upload & Template Download */}
      <div className="flex flex-wrap items-center justify-between mb-4 gap-3">
        {/* Left: Back Button */}
        <div className="flex">
          <button
            onClick={() => navigate(-1)}
            className="bg-yellow-500 text-white px-4 py-2 rounded hover:bg-yellow-600"
          >
            Back
          </button>
        </div>

        {/* Center: Page Title */}
        <div className="flex flex-1 justify-center">
          <h1 className="text-xl sm:text-2xl font-bold text-center">
            Add BOQ Items
          </h1>
        </div>

        {/* Right: Upload + Download */}
        <div className="flex gap-2">
          {/* Upload Excel */}
          <div className="">
            <input
              type="file"
              accept=".xlsx, .xls"
              onChange={handleExcelUpload}
              className="w-0 h-0 opacity-0"
              id="excelUpload"
            />
            <label
              htmlFor="excelUpload"
              className="cursor-pointer bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 relative top-1 rounded shadow"
            >
              Upload Excel
            </label>
          </div>

          {/* Download Template */}
          <button
            onClick={downloadTemplate}
            className="bg-green-500 hover:bg-green-600 text-white px-4 py-2 rounded shadow"
          >
            Download Template
          </button>
        </div>
      </div>

      {/* BOQ Items List */}
      <div className="space-y-4">
        {items.map((item, index) => (
          <div
            key={index}
            className="border rounded-lg p-4 shadow-sm bg-white space-y-3"
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
              <div>
                <input
                  type="text"
                  placeholder="SN"
                  value={item.sn}
                  onChange={(e) => handleChange(index, "sn", e.target.value)}
                  className={`border rounded px-2 py-1 w-full ${errors[index]?.sn ? "border-red-500" : ""}`}
                />
                {errors[index]?.sn && (
                  <p className="text-red-500 text-sm">{errors[index].sn}</p>
                )}
              </div>

              <div className="sm:col-span-1 md:col-span-2">
                <input
                  type="text"
                  placeholder="Item Name"
                  value={item.item_name}
                  onChange={(e) =>
                    handleChange(index, "item_name", e.target.value)
                  }
                  className={`border rounded px-2 py-1 w-full ${errors[index]?.item_name ? "border-red-500" : ""}`}
                />
                {errors[index]?.item_name && (
                  <p className="text-red-500 text-sm">
                    {errors[index].item_name}
                  </p>
                )}
              </div>

              <div>
                <input
                  type="text"
                  placeholder="Unit"
                  value={item.unit}
                  onChange={(e) => handleChange(index, "unit", e.target.value)}
                  className={`border rounded px-2 py-1 w-full ${errors[index]?.unit ? "border-red-500" : ""}`}
                />
                {errors[index]?.unit && (
                  <p className="text-red-500 text-sm">{errors[index].unit}</p>
                )}
              </div>

              <div>
                <input
                  type="number"
                  placeholder="Qty"
                  value={item.quantity}
                  onChange={(e) =>
                    handleChange(index, "quantity", e.target.value)
                  }
                  className={`border rounded px-2 py-1 w-full ${errors[index]?.quantity ? "border-red-500" : ""}`}
                />
                {errors[index]?.quantity && (
                  <p className="text-red-500 text-sm">
                    {errors[index].quantity}
                  </p>
                )}
              </div>

              <div>
                <input
                  type="number"
                  placeholder="Rate"
                  value={item.rate}
                  onChange={(e) => handleChange(index, "rate", e.target.value)}
                  className={`border rounded px-2 py-1 w-full ${errors[index]?.rate ? "border-red-500" : ""}`}
                />
                {errors[index]?.rate && (
                  <p className="text-red-500 text-sm">{errors[index].rate}</p>
                )}
              </div>

              <div>
                <input
                  type="text"
                  placeholder="Total"
                  value={item.total_amount}
                  readOnly
                  className="border rounded px-2 py-1 w-full bg-gray-100"
                />
              </div>
            </div>

            <textarea
              placeholder="Description"
              value={item.description}
              onChange={(e) =>
                handleChange(index, "description", e.target.value)
              }
              className="border rounded px-2 py-1 w-full"
              rows={2}
            ></textarea>
            {errors[index]?.description && (
              <p className="text-red-500 text-sm">
                {errors[index].description}
              </p>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
              <input
                type="text"
                placeholder="Scope"
                value={item.scope}
                onChange={(e) => handleChange(index, "scope", e.target.value)}
                readOnly // <--- This makes it non-editable
                className="border rounded px-2 py-1 w-full bg-gray-100 cursor-not-allowed" // <--- Added styling
              />
              <input
                type="text"
                placeholder="Approved Make"
                value={item.approved_make}
                onChange={(e) =>
                  handleChange(index, "approved_make", e.target.value)
                }
                className="border rounded px-2 py-1 w-full"
              />
              <input
                type="text"
                placeholder="Offered Make"
                value={item.offered_make}
                onChange={(e) =>
                  handleChange(index, "offered_make", e.target.value)
                }
                className="border rounded px-2 py-1 w-full"
              />
            </div>

            <div className="flex justify-end mt-2">
              {items.length > 1 && (
                <button
                  onClick={() => removeItem(index)}
                  className="bg-red-500 text-white px-3 py-1 rounded hover:bg-red-600"
                >
                  Remove
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 mt-6 justify-start">
        <button
          onClick={addItem}
          className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
        >
          Add Item
        </button>
        <button
          onClick={saveItems}
          className="bg-green-500 text-white px-4 py-2 rounded hover:bg-green-600"
        >
          Save All
        </button>
        {/* Cancel (Refresh page / reset form) */}
        <button
          onClick={() => window.location.reload()}
          className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
